import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface CardBinCheckResponse {
  bin: string;
  found: boolean;
  isMaximum: boolean;
  isCredit: boolean;
  isInstallmentEligible: boolean;
  bankName?: string;
  brand?: string;
  cardType?: string;
}

@Injectable({ providedIn: 'root' })
export class CardBinService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;
  private readonly cache = new Map<string, Observable<CardBinCheckResponse>>();

  /**
   * Kart numarasının ilk hanelerinden BIN kontrolü. Aynı BIN için sonuç cache'lenir.
   * Hata durumunda "not found" döner — caller fallback yapmalı.
   */
  check(binOrCardNumber: string): Observable<CardBinCheckResponse> {
    const digits = (binOrCardNumber ?? '').replace(/\D/g, '');
    if (digits.length < 6) {
      return of({ bin: digits, found: false, isMaximum: false, isCredit: false, isInstallmentEligible: false });
    }
    const prefix = digits.substring(0, Math.min(8, digits.length));

    const cached = this.cache.get(prefix);
    if (cached) return cached;

    const obs = this.http
      .get<CardBinCheckResponse>(`${this.apiUrl}/CardBin/check`, { params: { bin: prefix } })
      .pipe(
        catchError(() =>
          of<CardBinCheckResponse>({
            bin: prefix,
            found: false,
            isMaximum: false,
            isCredit: false,
            isInstallmentEligible: false,
          })
        ),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this.cache.set(prefix, obs);
    return obs;
  }
}
